import { Box, Button, Stack, styled, Typography } from "@mui/material";
import AvatarWithInitials from "../../common/AvatarWithInitials";
import { Replay } from "@mui/icons-material";
import PropTypes from "prop-types";
import { useState } from "react";
import { useSelector } from "react-redux";
import theme from "../../../theme";
import EditProfile from "./EditProfile";
import ResetPasswordModal from "./ResetPasswordModal";
import { toTitleCase } from "../../../utils/commonUtils";

const ProfileInfo = ({ label = "", icon = "" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    {icon && <Box component="img" src={icon} height="24px" width="24px" />}
    <Typography variant="body2" sx={{ fontSize: "14px", color: theme.palette.gray.medium }}>
      {label}
    </Typography>
  </Box>
);

export const getGenderIcon = (sex) => {
  const genderIcons = {
    male: "/assets/images/maleGender.svg",
    female: "/assets/images/femaleGender.svg",
    other: "/assets/images/otherGender.svg",
    default: "/assets/images/neutralGender.svg",
  };
  return genderIcons[sex?.toLowerCase() || ""] ?? genderIcons.default;
};

const DisplayDetails = ({ label, value }) => (
  <Stack gap="4px">
    <Typography fontSize="14px" color={theme.palette.gray.light}>
      {label}
    </Typography>
    <Typography fontSize="14px" color={theme.palette.black.light}>
      {value}
    </Typography>
  </Stack>
);

const StyledButton = styled(Button)(({ theme }) => ({
  width: "158px",
  height: "48px",
  backgroundColor: theme.palette.primary.main,
  borderRadius: "12px",
  color: theme.palette.common.white,
  fontWeight: "700",
}));

const UserProfile = ({ refetchData }) => {
  const {
    accountOwnerDetails: {
      profileImage,
      role,
      firstName,
      lastName,
      email,
      phone,
      address,
      companyName,
      companyNumber,
      age,
      sex,
    },
  } = useSelector((state) => state.settings);

  const [resetPassword, setResetPassword] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);

  const handleResetPassword = () => setResetPassword(!resetPassword);

  const handleEditProfile = () => setShowEditProfile(!showEditProfile);

  const handleReplayTour = async () => {
    try {
      const { TourActions } = await import("../../api/RequestUtil");
      const { setTourProgress } = await import("../../../redux/features/userSlice");
      const { store } = await import("../../../redux/store");

      // Reset tour on backend
      await TourActions.resetTour();

      // Fetch updated tour progress
      const response = await TourActions.getTourProgress();
      if (response.data?.status === "success") {
        // Update Redux with reset tour progress
        store.dispatch(setTourProgress(response.data.data));

        // Clear the dismissed state from localStorage
        localStorage.removeItem("checklistDismissed");

        // Reload page to restart tour
        window.location.reload();
      }
    } catch (error) {
      console.error("Error replaying tour:", error);
    }
  };

  return (
    <>
      <Stack paddingLeft={1}>
        <Stack
          data-tour="profile-section"
          borderRadius="24px"
          maxWidth="682px"
          width="100%"
          marginTop="20px"
          border={`1px solid ${theme.palette.white.light}`}
          padding={"16px 24px"}
          gap="16px"
        >
          <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <AvatarWithInitials
                name={`${firstName} ${lastName}`}
                alt={`${firstName} ${lastName}`}
                src={profileImage}
                sx={{ width: 72, height: 72 }}
              />
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.gray.medium, fontSize: "14px" }}>
                  {toTitleCase(role)}
                </Typography>
                <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.charcoal, fontSize: "16px" }}>
                    {`${firstName} ${lastName}`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: 0.5 }}>
                  <ProfileInfo label={toTitleCase(sex)} icon={getGenderIcon(sex)} />
                  <ProfileInfo label={age} icon={"/assets/images/cake.svg"} />
                </Box>
              </Box>
            </Box>
            <Box
              component="img"
              src={"/assets/images/edit.svg"}
              alt="edit icon"
              sx={{ cursor: "pointer" }}
              marginBottom={0.2}
              onClick={handleEditProfile}
            />
          </Box>

          <Stack
            sx={{ background: theme.palette.gray.lighter }}
            width="100%"
            borderRadius="12px"
            padding="12px"
            gap="19px"
          >
            <DisplayDetails label="Email" value={email} />
            <DisplayDetails label="Phone" value={phone} />
            <DisplayDetails label="Address" value={address} />
            <DisplayDetails label="Company Name" value={companyName} />
            <DisplayDetails label="Company Number" value={companyNumber} />
          </Stack>

          <Box sx={{ display: "flex", gap: 2 }}>
            <StyledButton onClick={handleResetPassword}>Reset Password</StyledButton>
            <StyledButton onClick={handleReplayTour} startIcon={<Replay />}>
              Replay Tour
            </StyledButton>
          </Box>
        </Stack>
      </Stack>
      <ResetPasswordModal open={resetPassword} handleClose={handleResetPassword} />

      {showEditProfile && (
        <EditProfile open={showEditProfile} handleClose={handleEditProfile} refetchData={refetchData} />
      )}
    </>
  );
};

ProfileInfo.propTypes = {
  label: PropTypes.string,
  icon: PropTypes.string,
};
DisplayDetails.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
};

UserProfile.propTypes = {
  refetchData: PropTypes.func,
};

export default UserProfile;
